<template>
  <v-autocomplete
    :loading="isLoading"
    outlined
    dense
    hide-details
    :style="customStyle"
    :class="customClass"
    :label="label"
    :placeholder="placeholder"
    v-model="selectedClient"
    :items="clients"
    item-text="name"
    item-value="id"
    :filter="customFilter"
    clearable
    auto-select-first
    attach
    :search-input.sync="searchQuery"
    :no-data-text="
      $has(permissions.Clients.Create) ? 'Press Enter to create a new client' : 'No clients found'
    ">
    <template v-slot:no-data v-if="$has(permissions.Clients.Create)">
      <v-list-item @click="addNewClient" :disabled="!searchQuery">
        <v-list-item-content>
          <v-list-item-title>
            Create "<strong>{{ searchQuery }}</strong
            >"
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>
    <template v-slot:item="{ item }">
      <div class="d-flex align-center justify-space-between" style="width: 100%">
        <span>{{ item.name }}</span>
        <v-btn
          v-if="$has(permissions.Clients.Delete)"
          icon
          x-small
          color="error"
          class="ml-2"
          @click.stop="confirmDeleteClient(item)">
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </div>
    </template>
  </v-autocomplete>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import permissions from '@/features/Share/config/permissions';
  import { $has } from '@/utils';
  import { log, warn, error } from '@/utils/debugLogger';

  export default {
    name: 'ClientSelector',
    props: {
      value: {
        type: String,
        default: null,
      },
      label: {
        type: String,
        default: 'Client',
      },
      placeholder: {
        type: String,
        default: 'Select or create a client',
      },
      customStyle: {
        type: String,
        default: 'width: 100%',
      },
      customClass: {
        type: String,
        default: 'v-min-input',
      },
      companyId: {
        type: String,
        default: null,
      },
    },
    data() {
      return {
        permissions,
        isLoading: false,
        searchQuery: null,
        selectedClient: null,
      };
    },
    computed: {
      ...mapGetters('CLIENT', ['GET_CLIENTS']),
      clients() {
        return this.GET_CLIENTS;
      },
      effectiveCompanyId() {
        return this.companyId || this.$store.getters['ACCOUNT/GET_COMPANY']?.id;
      },
    },
    mounted() {
      this.$store.dispatch('CLIENT/GET_CLIENTS', this.effectiveCompanyId);
    },
    watch: {
      value: {
        immediate: true,
        handler(newVal) {
          this.selectedClient = newVal;
        },
      },
      selectedClient(newVal) {
        this.$emit('input', newVal);
      },
    },
    methods: {
      $has,

      ...mapActions('CLIENT', ['ADD_CLIENT', 'DELETE_CLIENT']),

      customFilter(item, queryText) {
        const text = item.name.toLowerCase();
        const searchText = queryText.toLowerCase();
        return text.indexOf(searchText) > -1;
      },

      addNewClient() {
        log('ClientSelector', 'addNewClient', this.searchQuery);
        if (!this.searchQuery) return;

        if (!this.$has(permissions.Clients.Create)) return;

        // Check if client with same name already exists
        if (
          !this.clients.some((client) => client.name.toLowerCase() === this.searchQuery.toLowerCase())
        ) {
          let client = {
            name: this.searchQuery,
          };

          this.isLoading = true;

          // Pass client data and companyId as an object
          this.ADD_CLIENT({
            data: client,
            companyId: this.effectiveCompanyId
          })
            .then((c) => {
              this.selectedClient = c.id;
              this.searchQuery = '';
              this.$emit('client-added', c);
            })
            .finally(() => {
              this.isLoading = false;
            });
        }
      },

      confirmDeleteClient(client) {
        if (!this.$has(permissions.Clients.Delete)) return;

        this.$dialog.error({
          text: `Are you sure you want to delete the client "${client.name}"?`,
          title: 'Delete Client',
          actions: {
            false: {
              text: 'Cancel',
              color: 'primary',
              handle: () => {
              },
            },
            true: {
              text: 'Yes',
              color: 'error',
              handle: () => {
                this.deleteClient(client);
              },
            },
          },
        });
      },

      deleteClient(client) {
        this.isLoading = true;

        // Pass client id and companyId as an object
        this.DELETE_CLIENT({
          id: client.id,
          companyId: this.effectiveCompanyId
        })
          .then(() => {
            if (this.selectedClient === client.id) {
              this.selectedClient = null;
            }
            this.$emit('client-deleted', client);
          })
          .catch((err) => {
            error('ClientSelector', 'Failed to delete client', err);
            this.$emit('error', 'Failed to delete client');
          })
          .finally(() => {
            this.isLoading = false;
          });
      },
    },
  };
</script>

<style scoped>
  /* Add some styling to prevent delete button from affecting selection layout */
  .v-list-item__content {
    overflow: visible;
  }
</style> 